import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Sparkles, Send, Archive, CheckCircle2, RotateCcw, Music, Heart, X, RefreshCw } from "lucide-react";
import { api } from "@/lib/api";
import { Badge, ETYPE_META, STATUS_META, FilterTabs, timeAgo } from "./engShared";

const TONES = ["friendly", "professional", "short", "promotional", "apologetic"];

export default function EngInbox() {
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [status, setStatus] = useState("all");
  const [etype, setEtype] = useState("all");
  const [q, setQ] = useState("");
  const [sel, setSel] = useState(null);
  const [reply, setReply] = useState("");
  const [tone, setTone] = useState("friendly");
  const [busy, setBusy] = useState("");

  const load = () => api.get(`/engagement/inbox?status=${status}&etype=${etype}&q=${encodeURIComponent(q)}`)
    .then((r) => { setItems(r.data.items || []); setCounts(r.data.counts || {}); });
  useEffect(() => { load(); }, [status, etype]); // eslint-disable-line

  const act = async (id, action, extra = {}) => {
    try {
      const r = await api.post(`/engagement/inbox/${id}/action`, { action, ...extra });
      toast.success(action === "convert_request" ? "Converted to song request" :
        action === "convert_dedication" ? "Converted to dedication" : `Marked ${action}d`.replace("ed", "e") + "d");
      load(); if (sel?.message_id === id) setSel(null);
      return r.data;
    } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };

  const sendReply = async (sendEmail) => {
    if (!reply.trim()) return toast.error("Write a reply first");
    setBusy("reply");
    try {
      const r = await api.post(`/engagement/inbox/${sel.message_id}/reply`, { reply_text: reply, send_email: sendEmail });
      toast.success(r.data.emailed ? "Reply emailed to the listener" : "Reply saved");
      setReply(""); setSel(null); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Reply failed"); }
    setBusy("");
  };

  const aiReply = async () => {
    setBusy("ai");
    try {
      const r = await api.post(`/engagement/inbox/${sel.message_id}/ai-reply`, { tone });
      setReply(r.data.draft || "");
    } catch (e) { toast.error(e?.response?.data?.detail || "AI draft failed"); }
    setBusy("");
  };

  const syncSocial = async () => {
    setBusy("sync");
    try {
      const r = await api.post("/engagement/social-inbox/sync");
      const errs = Object.entries(r.data.platforms || {}).filter(([, v]) => v.error);
      toast.success(`Social sync done — ${r.data.imported} new item${r.data.imported === 1 ? "" : "s"}`);
      errs.forEach(([p, v]) => toast.info(`${p}: ${v.error}`, { duration: 6000 }));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Sync failed"); }
    setBusy("");
  };

  return (
    <div className="space-y-4" data-testid="eng-inbox-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold">Engagement Inbox</h1>
          <p className="text-sm text-ink-secondary">Every listener message, AI-classified, in one queue.</p>
        </div>
        <button onClick={syncSocial} disabled={busy === "sync"} data-testid="eng-inbox-sync-social"
          className="inline-flex items-center gap-1.5 rounded-full border border-emerald-500/50 text-emerald-300 px-4 py-2 text-xs font-bold disabled:opacity-50">
          {busy === "sync" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />} Sync social
        </button>
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <FilterTabs tabs={[["all", "All"], ["new", "New"], ["resolved", "Resolved"], ["archived", "Archived"]]}
          active={status} onChange={setStatus} counts={counts} />
        <select value={etype} onChange={(e) => setEtype(e.target.value)} data-testid="eng-inbox-type-filter"
          className="rounded-md border border-line bg-bg-elevated px-2 py-1.5 text-xs">
          <option value="all">All types</option>
          {Object.entries(ETYPE_META).map(([k, [label]]) => <option key={k} value={k}>{label}</option>)}
        </select>
        <form onSubmit={(e) => { e.preventDefault(); load(); }} className="flex gap-1.5">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search…" data-testid="eng-inbox-search"
            className="rounded-md border border-line bg-bg-elevated px-2.5 py-1.5 text-xs w-44" />
        </form>
      </div>

      <div className="rounded-xl border border-line bg-bg-surface divide-y divide-line/50">
        {!items.length && <div className="p-8 text-center text-sm text-ink-muted" data-testid="eng-inbox-empty">No messages here yet. Share your public listener page to start the conversation.</div>}
        {items.map((m) => (
          <button key={m.message_id} onClick={() => { setSel(m); setReply(""); }} data-testid={`eng-inbox-row-${m.message_id}`}
            className="w-full text-left px-4 py-3 hover:bg-bg-elevated/40 transition flex items-start gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-semibold">{m.listener_name}</span>
                <Badge meta={ETYPE_META[m.etype]} />
                <Badge meta={STATUS_META[m.status]} />
                {m.replies?.length > 0 && <span className="text-[10px] text-emerald-300 font-mono">{m.replies.length} repl{m.replies.length > 1 ? "ies" : "y"}</span>}
              </div>
              <div className="text-xs text-ink-secondary mt-0.5 line-clamp-2">{m.message}</div>
              <div className="text-[10px] text-ink-muted mt-0.5">{(m.source || "").replace(/_/g, " ")} · {timeAgo(m.created_at)}{m.email ? ` · ${m.email}` : ""}</div>
            </div>
          </button>
        ))}
      </div>

      {sel && (
        <div className="fixed inset-0 z-50 bg-black/60 flex justify-end" data-testid="eng-inbox-detail">
          <div className="w-full max-w-md h-full bg-bg-surface border-l border-line overflow-y-auto p-5 space-y-4">
            <div className="flex items-start justify-between gap-2">
              <div>
                <div className="font-display font-bold">{sel.listener_name}</div>
                <div className="text-[11px] text-ink-muted">{sel.email || "no email"} · {timeAgo(sel.created_at)} · via {sel.source}</div>
                <div className="flex gap-1.5 mt-1.5"><Badge meta={ETYPE_META[sel.etype]} /><Badge meta={STATUS_META[sel.status]} /></div>
              </div>
              <button onClick={() => setSel(null)} data-testid="eng-detail-close" className="text-ink-muted hover:text-ink-primary"><X className="h-5 w-5" /></button>
            </div>
            <div className="rounded-lg border border-line bg-bg-elevated p-3 text-sm whitespace-pre-wrap">{sel.message}</div>
            {(sel.replies || []).map((r) => (
              <div key={r.reply_id} className="rounded-lg border border-emerald-500/30 bg-emerald-500/5 p-3">
                <div className="text-[10px] text-emerald-300 font-mono mb-1">{r.by} · {timeAgo(r.created_at)}{r.emailed ? " · emailed" : ""}</div>
                <div className="text-xs whitespace-pre-wrap">{r.text}</div>
              </div>
            ))}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="font-mono text-[10px] uppercase tracking-wider text-ink-muted">Reply</span>
                <div className="flex items-center gap-1.5">
                  <select value={tone} onChange={(e) => setTone(e.target.value)} data-testid="eng-reply-tone"
                    className="rounded border border-line bg-bg-elevated px-1.5 py-0.5 text-[10px] capitalize">
                    {TONES.map((t) => <option key={t} value={t}>{t}</option>)}
                  </select>
                  <button onClick={aiReply} disabled={!!busy} data-testid="eng-ai-reply-btn"
                    className="inline-flex items-center gap-1 rounded-full border border-brand-amber/50 text-brand-amber px-2 py-0.5 text-[10px] font-bold">
                    {busy === "ai" ? <Loader2 className="h-3 w-3 animate-spin" /> : <Sparkles className="h-3 w-3" />} AI draft
                  </button>
                </div>
              </div>
              <textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={4} data-testid="eng-reply-text"
                placeholder="Write or AI-draft a reply… AI never sends automatically — you approve it."
                className="w-full rounded-md border border-line bg-bg-elevated px-2.5 py-2 text-xs" />
              <div className="flex gap-2">
                <button onClick={() => sendReply(true)} disabled={!!busy || !sel.email} data-testid="eng-reply-email-btn"
                  className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-full bg-brand-amber text-black px-3 py-2 text-xs font-bold disabled:opacity-40">
                  {busy === "reply" ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Email reply
                </button>
                <button onClick={() => sendReply(false)} disabled={!!busy} data-testid="eng-reply-save-btn"
                  className="rounded-full border border-line px-3 py-2 text-xs font-bold">Save only</button>
              </div>
              {!sel.email && <div className="text-[10px] text-ink-muted">Listener left no email — reply can only be saved internally.</div>}
            </div>
            <div className="flex flex-wrap gap-1.5 pt-1">
              <button onClick={() => act(sel.message_id, "resolve")} data-testid="eng-act-resolve"
                className="inline-flex items-center gap-1 rounded-full border border-line px-3 py-1.5 text-[11px]"><CheckCircle2 className="h-3 w-3" /> Resolve</button>
              <button onClick={() => act(sel.message_id, "archive")} data-testid="eng-act-archive"
                className="inline-flex items-center gap-1 rounded-full border border-line px-3 py-1.5 text-[11px]"><Archive className="h-3 w-3" /> Archive</button>
              <button onClick={() => act(sel.message_id, "reopen")} data-testid="eng-act-reopen"
                className="inline-flex items-center gap-1 rounded-full border border-line px-3 py-1.5 text-[11px]"><RotateCcw className="h-3 w-3" /> Reopen</button>
              <button onClick={() => act(sel.message_id, "convert_request")} data-testid="eng-act-convert-request"
                className="inline-flex items-center gap-1 rounded-full border border-brand-cyan/50 text-brand-cyan px-3 py-1.5 text-[11px]"><Music className="h-3 w-3" /> To Song Request</button>
              <button onClick={() => act(sel.message_id, "convert_dedication")} data-testid="eng-act-convert-dedication"
                className="inline-flex items-center gap-1 rounded-full border border-pink-400/50 text-pink-300 px-3 py-1.5 text-[11px]"><Heart className="h-3 w-3" /> To Dedication</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
